import { useState } from 'react';
import { css } from '@emotion/react';
import { PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useQueryClient } from 'react-query';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { theme } from '@frontend/theme';
import {
  Heading,
  TextField,
  useAlert,
  useForm,
  Text,
  DropdownField,
  MultiStep,
  MultiStepModalProps,
} from '@frontend/design-system';
import { invalidateListOverrideQueries } from '../../query-keys';
import { trackingId } from '../../tracking';
import { AddedExtension, ExtensionsAdder } from './extensions-adder';
import { PhoneNumbersTable } from './phone-numbers-table';

type CreateResponse = CallRouteV1.Types.CreateIO['output'];
type CreateCallRouteModalProps = {
  tenantLocation: SettingsTenantLocation;
  unallocatedPhoneNumbers: PhoneNumber[];
  onSuccess?: (data: CreateResponse) => void;
} & MultiStepModalProps;
export const CreateCallRouteModal = ({
  tenantLocation,
  unallocatedPhoneNumbers,
  onSuccess,
  ...createCallRouteModalProps
}: CreateCallRouteModalProps) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const [selectedPhoneNumberIds, setSelectedPhoneNumberIds] = useState<string[]>([]);
  const [extensions, setExtensions] = useState<string[]>([]);
  const emptyExtension: AddedExtension = { id: 1, value: '', isValid: false };
  const [addedExtension, setAddedExtension] = useState<AddedExtension[]>([emptyExtension]);
  // Store when the callroute has been created successfully so that we can disable the save button
  // to prevent multiple submissions while the modal is closing.
  const [isSuccess, setIsSuccess] = useState(false);
  const onClose = createCallRouteModalProps.modalProps.onClose;
  const isUnify = tenantLocation.locationType === 'unify';

  const form = useForm({
    fields: {
      name: {
        type: 'text',
        required: true,
        value: '',
      },
      location: {
        type: 'dropdown',
        required: isUnify,
      },
    },
  });
  const textFieldProps = form.getFieldProps('name');

  const { mutate: createCallRouteMutation, isLoading: createIsLoading } = CallRouteV1.Mutations.useCreateMutation({
    options: {
      onMutate: () => {},
      onSuccess: (data) => {
        invalidateListOverrideQueries(queryClient, phoneTenantId);
        alerts.success('Call Route created successfully.');
        setIsSuccess(true);
        onClose();
        onSuccess?.(data);
      },
      onError: () => {
        alerts.error('Failed to create Call Route.');
      },
    },
  });

  const createCallRoute = (formValues: typeof form.values, tenantId: string, exts: string[]) => {
    createCallRouteMutation({
      tenantId: tenantId,
      name: formValues.name ?? '',
      locationId: formValues.location ?? '',
      phoneNumberIds: selectedPhoneNumberIds,
      extensionNumbers: exts.map((ext) => parseInt(ext)),
    });
  };

  const cancel = () => {
    form.reset();
    setExtensions([]);
    setSelectedPhoneNumberIds([]);
    setAddedExtension([emptyExtension]);
    onClose();
  };

  const handleExtensionsStepDone = () => {
    const validExtensions = addedExtension
      .filter((extension2) => extension2.isValid)
      .map((extension2) => extension2.value);
    const uniqueExtensions = [...new Set(validExtensions)];
    setExtensions(uniqueExtensions);
    if (!createIsLoading && !isSuccess) {
      createCallRoute(form.values, tenantLocation.phoneTenantId ?? '', uniqueExtensions);
    }
  };

  return (
    <MultiStep.Modal minWidth={800} {...createCallRouteModalProps} onCancel={cancel}>
      {/* Step 1: Call Route Name */}
      <MultiStep.Step
        id='name'
        header={{ title: t('Create Call Route') }}
        label={t('Call Route Name')}
        isValid={textFieldProps.value !== ''}
        footer={{ secondary: t('Cancel'), onClickSecondary: cancel }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Heading level={3}>{t('Call Route Name')}</Heading>
          <div css={{ width: '318px', paddingLeft: theme.spacing(0.25) }}>
            <TextField
              {...textFieldProps}
              label={t('Call Route Name')}
              name='name'
              data-trackingid={trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'create::route-name-input',
              })}
            />
          </div>
        </div>
      </MultiStep.Step>

      {/* Step 2: Assign Phone Numbers */}
      <MultiStep.Step
        css={{ maxWidth: '537px' }}
        id='numbers'
        header={{ title: t('Create Call Route') }}
        label={t('Assign Phone Numbers')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: cancel,
          helpText:
            selectedPhoneNumberIds.length > 0
              ? selectedPhoneNumberIds.length > 1
                ? t('{{count}} phone numbers selected', { count: selectedPhoneNumberIds.length })
                : t('{{count}} phone number selected', { count: selectedPhoneNumberIds.length })
              : undefined,
        }}
      >
        <div>
          <div
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
          >
            <Heading level={3}>{t('Assign Phone Numbers')}</Heading>
            <Text size='large'>{t('Assign phone numbers to this Call Route so it can receive calls.')}</Text>
          </div>
          <div css={{ padding: theme.spacing(0, 0.25) }}>
            <PhoneNumbersTable
              tenantLocation={tenantLocation}
              phoneNumbers={unallocatedPhoneNumbers}
              selectedPhoneNumberIds={selectedPhoneNumberIds}
              setSelectedPhoneNumberIds={setSelectedPhoneNumberIds}
              trackingIdBase={trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'create::phone-numbers',
              })}
            />
          </div>
        </div>
      </MultiStep.Step>

      {/* Step 3: Assign Extensions */}
      <MultiStep.Step
        isValid={addedExtension.every((ext) => ext.isValid || !ext.value)}
        id='extensions'
        header={{ title: t('Create Call Route') }}
        label={t('Assign Route Extensions')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: cancel,
          onClickPrimary: isUnify ? () => createCallRouteModalProps.goNextStep() : handleExtensionsStepDone,
        }}
      >
        <div>
          <div
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
          >
            <Heading level={3}>{t('Assign Route Extensions')}</Heading>
            <Text size='large'>
              {t(
                'Assign extensions to this Call Route so that your staff can call or transfer callers to this Call Route.'
              )}
            </Text>
          </div>
          <div css={{ paddingLeft: theme.spacing(0.25) }}>
            <ExtensionsAdder
              extensions={addedExtension}
              setExtensions={setAddedExtension}
              tenantLocation={tenantLocation}
              initialExtensions={extensions}
              trackingIdBase={trackingId({ context: 'setting', feature: 'call-routes', details: 'create::extensions' })}
            />
          </div>
        </div>
      </MultiStep.Step>

      {/* Step 4: Location (only if isUnify) */}
      {isUnify && (
        <MultiStep.Step
          id='location'
          header={{ title: t('Create Call Route') }}
          label={t('Location')}
          isValid={!!form.values.location}
          footer={{ secondary: t('Cancel'), onClickSecondary: cancel, onClickPrimary: handleExtensionsStepDone }}
        >
          <div>
            <div
              css={css`
                margin-bottom: ${theme.spacing(2)};
              `}
            >
              <Heading level={3}>{t('Location')}</Heading>
              <Text size='large'>{t('Select the location that this Call Route will belong to.')}</Text>
            </div>
            <div css={{ padding: theme.spacing(0, 0.25) }}>
              <DropdownField
                label={t('Select Location')}
                {...form.getFieldProps('location')}
                data-trackingid={trackingId({
                  context: 'setting',
                  feature: 'call-routes',
                  details: 'create::location-dropdown',
                })}
              >
                {tenantLocation.children?.map((location) => (
                  <DropdownField.Option
                    key={location.locationId}
                    value={location.locationId ?? ''}
                    trackingId={trackingId({
                      context: 'setting',
                      feature: 'call-routes',
                      details: 'create::location-menu-option',
                    })}
                  >
                    {location.name}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </div>
          </div>
        </MultiStep.Step>
      )}
    </MultiStep.Modal>
  );
};
